import { Component } from '@theme/component';

// Submits directly to the Ripple Cloud Function (submitShopifyLeadMagnet in
// ripple-js/functions/src/shopifyProducts.js) — not a native Liquid
// `{% form %}` tag, since this needs to reach an endpoint outside Shopify
// entirely and get a JSON list of files back to reveal inline. Firebase
// Callable Functions expect a `{"data": {...}}` request body and reply with
// `{"result": {...}}` (or `{"error": {...}}`) when invoked over plain HTTP
// instead of the Firebase SDK — there's no SDK available here.
const FUNCTIONS_BASE_URL = 'https://us-central1-ripplepage-prod.cloudfunctions.net';

class LeadMagnetForm extends Component {
  /** @type {AbortController | undefined} */
  #activeSubmit;

  connectedCallback() {
    super.connectedCallback();
  }

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit(event) {
    event.preventDefault();
    this.#submit();
  }

  async #submit() {
    const form = this.refs.form;
    const nameInput = this.refs.nameInput;
    const emailInput = this.refs.emailInput;
    const marketingInput = this.refs.marketingInput;
    if (!(form instanceof HTMLFormElement) || !(emailInput instanceof HTMLInputElement)) return;

    if (!form.reportValidity()) return;

    this.#activeSubmit?.abort();
    const abortController = new AbortController();
    this.#activeSubmit = abortController;

    this.setAttribute('data-state', 'submitting');
    this.#setError('');

    try {
      const response = await fetch(`${FUNCTIONS_BASE_URL}/submitShopifyLeadMagnet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: abortController.signal,
        body: JSON.stringify({
          data: {
            shopDomain: this.dataset.shopDomain,
            shopifyProductId: this.dataset.shopifyProductId,
            name: nameInput instanceof HTMLInputElement ? nameInput.value.trim() : '',
            email: emailInput.value.trim(),
            marketingOptIn: marketingInput instanceof HTMLInputElement ? marketingInput.checked : false,
          },
        }),
      });

      const payload = await response.json();

      if (!response.ok || payload.error) {
        throw new Error(payload.error?.message || 'Something went wrong. Please try again.');
      }

      this.#renderFiles(payload.result?.files || []);
      this.setAttribute('data-state', 'success');
    } catch (error) {
      if (abortController.signal.aborted) return;
      console.error('[lead-magnet] Submission failed:', error);
      this.#setError(error instanceof Error ? error.message : 'Something went wrong. Please try again.');
      this.setAttribute('data-state', 'idle');
    }
  }

  /**
   * @param {string} message
   */
  #setError(message) {
    const errorEl = this.refs.error;
    if (!(errorEl instanceof HTMLElement)) return;
    errorEl.textContent = message;
    errorEl.hidden = !message;
  }

  /**
   * @param {{ id: string, name: string, url: string, size: number | null }[]} files
   */
  #renderFiles(files) {
    const list = this.refs.fileList;
    if (!(list instanceof HTMLElement)) return;

    list.replaceChildren();

    for (const file of files) {
      const item = document.createElement('li');
      item.className = 'lead-magnet__file';

      const link = document.createElement('a');
      link.href = file.url;
      link.textContent = file.name;
      link.setAttribute('download', file.name);
      link.target = '_blank';
      link.rel = 'noopener noreferrer';

      item.appendChild(link);
      list.appendChild(item);
    }
  }
}

if (!customElements.get('lead-magnet-form')) {
  customElements.define('lead-magnet-form', LeadMagnetForm);
}
